import { useQuery } from '@tanstack/react-query'
import { GENERAL_API } from '../../../firebase/general-apis'

import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isoWeek from 'dayjs/plugin/isoWeek'
import weekday from 'dayjs/plugin/weekday'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isBetween from 'dayjs/plugin/isBetween'
import { BookingTerm } from '../../../firebase/types-bookings'
import { useSelector } from 'react-redux'
import { selectSchoolConfig } from '../../../redux/schoolSlice'
import { logger } from '../../../helpers/logger'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)

// Optional but recommended
const TIMEZONE = 'Australia/Melbourne'
dayjs.tz.setDefault(TIMEZONE)

dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.extend(isoWeek)
dayjs.extend(weekday)
dayjs.extend(isBetween)

export type TermDefinition = {
    key: BookingTerm
    start: dayjs.Dayjs
    end: dayjs.Dayjs
    noWeeks: number
}

export type WorkingDay = {
    date: string // YYYY-MM-DD
    reverseDate: string // DD-MM-YYYY
    formattedDate: string // Mon, 01 Jan
    isoWeek: string // 2026-W14
    isoWeekYear: string
    termKey: BookingTerm
    publicHoliday?: boolean
}

type ComputeArgs = {
    fromDate: dayjs.Dayjs
    termRanges: TermDefinition[]
    publicHolidays: string[] // YYYY-MM-DD
    count?: number
}

export type WorkingWeek = {
    term: BookingTerm
    week: string
    days: WorkingDay[]
}

export type CalendarObject = {
    terms: [BookingTerm, number][]
    flatDays: WorkingDay[]
    dateIndex: Record<string, WorkingDay>
    weeks: WorkingWeek[]
    range: {
        start: string
        end: string | undefined
    }
    current: {
        term: BookingTerm
        weekNo: number
    }
    publicHolidays: string[]
}

function extractOrderedTerms(
    termDates: Record<string, string>
): TermDefinition[] {
    // TODo Repeat for 4 terms
    const termOneStart = dayjs.tz(
        termDates.termOneStart,
        'DD-MM-YYYY',
        TIMEZONE
    )
    const termOneFinish = dayjs.tz(
        termDates.termOneFinish,
        'DD-MM-YYYY',
        TIMEZONE
    )
    const termTwoStart = dayjs.tz(
        termDates.termTwoStart,
        'DD-MM-YYYY',
        TIMEZONE
    )
    const termTwoFinish = dayjs.tz(
        termDates.termTwoFinish,
        'DD-MM-YYYY',
        TIMEZONE
    )
    const termThreeStart = dayjs.tz(
        termDates.termThreeStart,
        'DD-MM-YYYY',
        TIMEZONE
    )
    const termThreeFinish = dayjs.tz(
        termDates.termThreeFinish,
        'DD-MM-YYYY',
        TIMEZONE
    )
    const termFourStart = dayjs.tz(
        termDates.termFourStart,
        'DD-MM-YYYY',
        TIMEZONE
    )
    const termFourFinish = dayjs.tz(
        termDates.termFourFinish,
        'DD-MM-YYYY',
        TIMEZONE
    )

    return [
        {
            key: 'termOne',
            start: termOneStart,
            end: termOneFinish,
            noWeeks: termOneFinish.diff(termOneStart, 'weeks'),
        },
        {
            key: 'termTwo',
            start: termTwoStart,
            end: termTwoFinish,
            noWeeks: termTwoFinish.diff(termTwoStart, 'weeks'),
        },
        {
            key: 'termThree',
            start: termThreeStart,
            end: termThreeFinish,
            noWeeks: termThreeFinish.diff(termThreeStart, 'weeks'),
        },
        {
            key: 'termFour',
            start: termFourStart,
            end: termFourFinish,
            noWeeks: termFourFinish.diff(termFourStart, 'weeks'),
        },
    ]
}

function findEffectiveCurrentTerm(
    terms: TermDefinition[],
    today: dayjs.Dayjs
): number {
    // 1. If today is inside a term → use that term
    const activeIndex = terms.findIndex(
        (term) =>
            today.isSameOrAfter(term.start, 'day') &&
            today.isSameOrBefore(term.end, 'day')
    )

    if (activeIndex !== -1) {
        logger.debug(`Current term found: ${activeIndex}`)
        return activeIndex
    }

    // 2. Otherwise, we're in holidays → use the upcoming term
    const lastPastIndex = [...terms]
        .map((t, i) => ({ term: t, index: i }))
        .find(({ term }) => today.isBefore(term.start, 'day'))

    logger.debug(`Not in term: upcoming ${lastPastIndex?.index}`)

    // Fallback safety (shouldn't really happen)
    return lastPastIndex?.index ?? 0
}

function resolveTwoConsecutiveTerms(
    terms: TermDefinition[],
    currentIndex: number,
    nextYearTermOneStart?: string,
    nextYearTermOneFinish?: string
): TermDefinition[] {
    const previousAndCurrentTerms = terms.slice(0, currentIndex + 1)

    // Normal case: term 1–3
    if (currentIndex < 3) {
        return [...previousAndCurrentTerms, terms[currentIndex + 1]]
    }

    // Term 4 → include all terms in current year + term 1 of next year
    if (!nextYearTermOneStart || !nextYearTermOneFinish) {
        throw new Error(
            'Next year term one dates required for term four rollover'
        )
    }

    return [
        ...previousAndCurrentTerms,
        {
            key: 'termOne',
            start: dayjs(nextYearTermOneStart),
            end: dayjs(nextYearTermOneFinish),
            noWeeks: 0,
        },
    ]
}

export function computeUpcomingWorkingDaysForTerms({
    terms,
    publicHolidays,
}: {
    terms: TermDefinition[]
    publicHolidays: string[]
}): WorkingDay[] {
    const holidays = new Set(publicHolidays)
    const results: WorkingDay[] = []

    for (const term of terms) {
        let cursor = term.start.startOf('day')
        let currIsoWeek = 0
        let weekNo = 0

        while (cursor.isSameOrBefore(term.end, 'day')) {
            // Keep track of week per term
            // Increment if new iso week
            if (currIsoWeek !== cursor.isoWeek()) {
                currIsoWeek = cursor.isoWeek()
                weekNo++
            }
            const dateStr = cursor.format('YYYY-MM-DD')
            const reverseDateStr = cursor.format('DD-MM-YYYY')
            const isWeekend = cursor.isoWeekday() >= 6
            const isHoliday = holidays.has(reverseDateStr)

            if (!isWeekend) {
                results.push({
                    date: dateStr,
                    reverseDate: reverseDateStr,
                    formattedDate: cursor.format('ddd, DD MMM'),
                    isoWeekYear: `${cursor.isoWeekYear()}`,
                    isoWeek: `W${weekNo}`,
                    termKey: term.key,
                    publicHoliday: isHoliday ? true : undefined,
                })
            }

            cursor = cursor.add(1, 'day')
        }
    }

    return results
}

export function groupByWeek(days: WorkingDay[]): WorkingWeek[] {
    return Object.values(
        days.reduce<Record<string, WorkingWeek>>((acc, day) => {
            const key = `${day.termKey}-${day.isoWeek}`
            if (!acc[key]) {
                acc[key] = {
                    term: day.termKey,
                    week: day.isoWeek,
                    days: [],
                }
            }
            acc[key].days.push(day)
            return acc
        }, {})
    )
}

const useCalendarDates = () => {
    const config = useSelector(selectSchoolConfig)

    return useQuery({
        queryKey: [
            'calendar',
            config.data?.country,
            config.data?.locationState,
        ],
        queryFn: async () =>
            config.loaded ? GENERAL_API.fetchCalendarDates(config.data) : null,
        enabled: config.loaded,
        staleTime: 100000,
        select: (raw) => {
            const today = dayjs().tz()
            const orderedTerms = extractOrderedTerms(raw.termDates)

            const currentTermIndex = findEffectiveCurrentTerm(
                orderedTerms,
                today
            )

            const currentTerm = orderedTerms[currentTermIndex]
            let weekNo: number = 0
            if (
                today.isBetween(currentTerm.start, currentTerm.end, 'd', '[]')
            ) {
                weekNo =
                    dayjs(today).isoWeek() -
                    dayjs(currentTerm.start).isoWeek() +
                    1
            }

            const selectedTerms = resolveTwoConsecutiveTerms(
                orderedTerms,
                currentTermIndex,
                raw.termDates.nextYearStart,
                raw.termDates.nextYearTermOneFinish
            )

            const days = computeUpcomingWorkingDaysForTerms({
                terms: selectedTerms,
                publicHolidays: Object.values(raw.publicHolidays),
            })

            return {
                terms: selectedTerms.map((el) => [el.key, el.noWeeks]),
                flatDays: days,
                dateIndex: days.reduce(
                    (acc, curr) => ({ ...acc, [curr.reverseDate]: curr }),
                    {} as Record<string, WorkingDay>
                ),
                weeks: groupByWeek(days),
                range: {
                    start: days[0]?.date,
                    end: days.at(-1)?.date,
                },
                current: {
                    term: currentTerm.key,
                    weekNo,
                },
                publicHolidays: Object.values(raw.publicHolidays),
            } as CalendarObject
        },
    })
}

export default useCalendarDates
